import { CSSProperties, useRef, useEffect, useState } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	image: {
		src: string;
		alt?: string;
		height?: number | string;
		width?: number | string;
		caption?: string;
		style?: CSSProperties;
	};
	rootMargin?: string; // 로드 거리 설정
	forceLoad?: boolean; // 강제 로드 옵션
}

const LazyImage = ({ image, rootMargin = '200px 500px', forceLoad = false, ...props }: ImageProps) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [isInView, setIsInView] = useState(false);
	const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
	const [hasError, setHasError] = useState(false);
	const elementRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// 이미지 소스가 바뀌면 상태 초기화
		if (loadedSrc !== image.src) {
			setIsLoaded(false);
			setIsInView(false);
			setLoadedSrc(null);
			setHasError(false);
		}

		if (forceLoad || isInView) {
			if (!isLoaded && image.src !== loadedSrc) {
				setIsInView(true);
				const img = new Image();
				img.onload = () => {
					setIsLoaded(true);
					setLoadedSrc(image.src);
				};
				img.onerror = () => {
					setHasError(true);
				};
				img.src = image.src;
			}
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsInView(true);
					// 이미지 로드
					const img = new Image();
					img.onload = () => {
						setIsLoaded(true);
						setLoadedSrc(image.src);
					};
					img.onerror = () => {
						setHasError(true);
					};
					img.src = image.src;
					observer.unobserve(elementRef.current!);
				}
			},
			{
				rootMargin: rootMargin,
				threshold: 0.1,
			}
		);

		if (elementRef.current) {
			observer.observe(elementRef.current);
		}

		return () => {
			if (elementRef.current) {
				observer.unobserve(elementRef.current);
			}
		};
	}, [image.src, rootMargin, forceLoad, isLoaded, isInView, loadedSrc]);

	return (
		<div ref={elementRef} style={{ position: 'relative' }}>
			{isInView && (
				<img
					alt={image.alt}
					src={isLoaded ? image.src : undefined}
					height={image.height}
					width={image.width}
					style={{
						...image.style,
						filter: isLoaded ? 'none' : 'blur(5px)',
						transition: 'filter 0.3s ease-in-out, transform 0.3s ease-in-out',
					}}
					{...props}
				/>
			)}
			{/* <span>{image.caption}</span> */}
		</div>
	);
};

export default LazyImage;
